import React, { useEffect, useState } from 'react';
import { TrendingUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { unctadApi } from '../../../services/api';
import type { FDIFlowData } from '../../../services/api/unctadApi';

interface FDIFlowsPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const FDIFlowsPanel: React.FC<FDIFlowsPanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [data, setData] = useState<FDIFlowData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await unctadApi.getFDIFlows(['UZB']);
      if (response.data) {
        // Sort by year descending
        setData(response.data.sort((a, b) => b.year - a.year));
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  // Calculate YoY growth for latest year
  const latestYear = data[0];
  const previousYear = data[1];
  const yoyGrowth = latestYear && previousYear
    ? ((latestYear.inflows - previousYear.inflows) / previousYear.inflows) * 100
    : null;

  const exportData = data.map(d => ({
    year: d.year,
    inflows: d.inflows,
    outflows: d.outflows,
    netFDI: d.netFDI,
    source: d.source,
  }));

  return (
    <Panel
      id="fdi-flows"
      title="FDI Flows"
      subtitle="Uzbekistan"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col">
        {/* Source Badge */}
        <div className="flex items-center gap-2 mb-2 px-1">
          <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
          <span className="text-xs text-gray-500">World Investment Report 2024</span>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-xs">
            Loading FDI data...
          </div>
        ) : (
          <>
            {/* Headline Metrics */}
            {latestYear && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="p-2 bg-data-green/10 rounded border border-data-green/30">
                  <div className="text-xs text-gray-500 mb-1">FDI Inflows ({latestYear.year})</div>
                  <div className="text-xl font-mono font-bold text-data-green">
                    ${latestYear.inflows / 1000}B
                  </div>
                  {yoyGrowth !== null && (
                    <div className="flex items-center gap-1 mt-1">
                      {yoyGrowth > 0 ? (
                        <ArrowUpRight size={10} className="text-data-green" />
                      ) : (
                        <ArrowDownRight size={10} className="text-data-red" />
                      )}
                      <span className={`text-xs font-mono ${
                        yoyGrowth > 0 ? 'text-data-green' : 'text-data-red'
                      }`}>
                        {yoyGrowth > 0 ? '+' : ''}{yoyGrowth.toFixed(1)}% YoY
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-2 bg-terminal-border/30 rounded">
                  <div className="text-xs text-gray-500 mb-1">Net FDI ({latestYear.year})</div>
                  <div className="text-xl font-mono font-bold text-data-amber">
                    ${(latestYear.netFDI / 1000).toFixed(1)}B
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {(latestYear.netFDI / 1050).toFixed(1)}% of GDP
                  </div>
                </div>
              </div>
            )}

            {/* Historical Chart (simplified bar chart) */}
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-2">FDI Inflows Trend ($ millions)</div>
              <div className="space-y-1.5">
                {data.slice(0, 5).reverse().map(row => {
                  const maxInflows = Math.max(...data.map(d => d.inflows));
                  const width = (row.inflows / maxInflows) * 100;

                  return (
                    <div key={row.year} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-8">{row.year}</span>
                      <div className="flex-1 h-4 bg-terminal-border/30 rounded overflow-hidden">
                        <div
                          className="h-full bg-data-green transition-all duration-500"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-white w-14 text-right">
                        ${(row.inflows / 1000).toFixed(1)}B
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Context */}
            <div className="mt-2 p-2 bg-data-amber/10 rounded border border-data-amber/20">
              <div className="flex items-center gap-1 mb-1">
                <TrendingUp size={12} className="text-data-amber" />
                <span className="text-xs text-data-amber font-medium">
                  Central Asia's FDI Leader
                </span>
              </div>
              <p className="text-xs text-gray-400">
                Uzbekistan attracted ${latestYear?.inflows ? (latestYear.inflows / 1000).toFixed(1) : 'N/A'}B in FDI in {latestYear?.year || 'N/A'},
                making it the top FDI destination in Central Asia.
              </p>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-2 pt-1 border-t border-terminal-border/50 text-xs text-gray-600">
          Source: UNCTAD World Investment Report 2024
        </div>
      </div>
    </Panel>
  );
};

export default FDIFlowsPanel;
